export type RestriktionTyp = "forbud" | "rekommendation" | "ingen";

/** 'aktiv' = in force now, 'kommande' = announced with a future start date */
export type RestriktionPeriod = "aktiv" | "kommande";

export type NotisTyp = "introduktion" | "uppdatering" | "upphavning";

export interface KommunStatus {
  kommunkod: string;
  kommun_namn: string;
  lan: string | null;
  va_aktor_id: string | null;
  va_namn: string | null;
  va_webbplats: string | null;
  kalla_id: string | null;
  kalla_url: string | null;
  restriktion_id: string | null;
  typ: RestriktionTyp | null;
  omrade_nyckel: string | null;
  datum_fran: string | null;
  datum_till: string | null;
  omraden_text: string | null;
  regler_text: string | null;
  senast_scrapad: string | null;
  period: RestriktionPeriod | null;
}

export const RESTRIKTION_LABEL: Record<RestriktionTyp, string> = {
  forbud: "Bevattningsförbud",
  rekommendation: "Rekommendation",
  ingen: "Inga restriktioner",
};

export const RESTRIKTION_DESCRIPTION: Record<RestriktionTyp, string> = {
  forbud: "Det råder bevattningsförbud i detta område. Kontrollera med din VA-huvudman för detaljer.",
  rekommendation: "Det finns en rekommendation att begränsa vattenanvändningen i detta område.",
  ingen: "Det finns inga aktiva restriktioner just nu enligt VA-huvudmannens information.",
};

export const RESTRIKTION_CLASSES: Record<RestriktionTyp, { dot: string; text: string; border: string; bg: string }> = {
  forbud: {
    dot: "bg-status-active",
    text: "text-status-active",
    border: "border-status-active/30",
    bg: "bg-status-active-soft",
  },
  rekommendation: {
    dot: "bg-status-warning",
    text: "text-status-warning",
    border: "border-status-warning/30",
    bg: "bg-status-warning-soft",
  },
  ingen: {
    dot: "bg-status-none",
    text: "text-status-none",
    border: "border-status-none/30",
    bg: "bg-status-none-soft",
  },
};

export function worstRestriktionTyp(types: RestriktionTyp[]): RestriktionTyp | null {
  if (types.includes("forbud")) return "forbud";
  if (types.includes("rekommendation")) return "rekommendation";
  if (types.length === 0) return null;
  return "ingen";
}

/** Local date as YYYY-MM-DD — comparable lexically against datum_fran/datum_till. */
export function todayStr(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Restrictions rows that are actually in force (excludes announced future bans). */
export function inForceRows<T extends { period: RestriktionPeriod | null }>(rows: T[]): T[] {
  return rows.filter((r) => r.period !== "kommande");
}

/** Announced restrictions with a future start date. */
export function kommandeRows<T extends { restriktion_id: string | null; period: RestriktionPeriod | null }>(rows: T[]): T[] {
  return rows.filter((r) => r.restriktion_id !== null && r.period === "kommande");
}

/** The date a notis takes effect: lifts use datum_till, others use datum_fran. */
export function notisEffectiveDatum(
  notisTyp: NotisTyp,
  datumFran: string | null,
  datumTill: string | null
): string | null {
  return notisTyp === "upphavning" ? datumTill : datumFran;
}

export function notisEffectiveInFuture(
  notisTyp: NotisTyp,
  datumFran: string | null,
  datumTill: string | null
): boolean {
  const d = notisEffectiveDatum(notisTyp, datumFran, datumTill);
  return d !== null && d > todayStr();
}

/** Tense-aware effective-date label, e.g. "Upphör" vs "Upphörde". */
export function notisDateLabel(
  notisTyp: NotisTyp,
  datumFran: string | null,
  datumTill: string | null
): string {
  const future = notisEffectiveInFuture(notisTyp, datumFran, datumTill);
  if (notisTyp === "upphavning") return future ? "Upphör" : "Upphörde";
  return future ? "Börjar gälla" : "Gäller sedan";
}

export function formatDate(dateStr: string | null): string {
  if (!dateStr) return "Okänt datum";
  return new Date(dateStr).toLocaleDateString("sv-SE", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return "Okänd tid";
  
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return `för ${diffDays} ${diffDays === 1 ? "dag" : "dagar"} sedan`;
  }
  if (diffHours > 0) {
    return `för ${diffHours} ${diffHours === 1 ? "timme" : "timmar"} sedan`;
  }
  return "nyss";
}
